import { Spinner } from '@chakra-ui/react';
import { AuthLayout } from '@components/templates';
import { lazyWithReload } from '@shared/react/lazyWithReload';
import { SentryRoutes } from '@shared/router';
import { Suspense } from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';

import {
  acceptInvitationPath,
  authMagicLinkPath,
  emailVerificationPath,
  emailVerificationSuccessPath,
  forgotPasswordPath,
  loginPath,
  requestMagicLinkPath,
  resetPasswordPath,
  resetPasswordWithOrgPath,
  signupPath,
  verifyMagicLinkPath,
} from './urls';

const LoadingSpinner = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'fixed',
      inset: 0,
      width: '100vw',
      height: '100vh',
      background: 'white',
      zIndex: 2999,
    }}
  >
    <Spinner color="red.400" size="lg" />
  </div>
);

const LoginView = lazyWithReload(() =>
  import('./pages/Login').then((m) => ({ default: m.LoginView }))
);
const SignUpView = lazyWithReload(() =>
  import('./pages/Signup').then((m) => ({ default: m.SignUpView }))
);

const ForgotPasswordView = lazyWithReload(() =>
  import('./pages/ForgotPassword').then((m) => ({ default: m.ForgotPasswordView }))
);
const ResetPasswordView = lazyWithReload(() =>
  import('./pages/ResetPassword').then((m) => ({ default: m.ResetPasswordView }))
);
const AcceptInvitationView = lazyWithReload(() =>
  import('./pages/AcceptInvitation').then((m) => ({ default: m.AcceptInvitationView }))
);
const EmailVerificationView = lazyWithReload(() =>
  import('./pages/EmailVerification').then((m) => ({
    default: m.EmailVerificationView,
  }))
);

const EmailVerificationSuccessView = lazyWithReload(() =>
  import('./pages/EmailVerificationSuccess').then((m) => ({
    default: m.EmailVerificationSuccessView,
  }))
);

const RequestMagicLinkView = lazyWithReload(() =>
  import('./pages/RequestMagicLink').then((m) => ({ default: m.RequestMagicLinkView }))
);

const VerifyMagicLinkView = lazyWithReload(() =>
  import('./pages/VerifyMagicLink').then((m) => ({ default: m.VerifyMagicLinkView }))
);

const RedirectToLogin: React.FC = () => {
  const location = useLocation();
  const redirectUrl = location.pathname + location.search;
  const to =
    redirectUrl && redirectUrl !== '/'
      ? `${loginPath}?redirect-url=${encodeURIComponent(redirectUrl)}`
      : loginPath;

  return <Navigate to={to} replace />;
};

export const AuthRouter: React.FC = () => {
  return (
    <AuthLayout>
      <Suspense fallback={<LoadingSpinner />}>
        <SentryRoutes>
          <Route Component={LoginView} path={loginPath} />
          <Route Component={SignUpView} path={signupPath} />
          <Route Component={ForgotPasswordView} path={forgotPasswordPath} />
          <Route Component={ResetPasswordView} path={resetPasswordPath} />
          <Route Component={ResetPasswordView} path={resetPasswordWithOrgPath} />
          <Route Component={AcceptInvitationView} path={acceptInvitationPath} />
          <Route Component={EmailVerificationView} path={emailVerificationPath} />
          <Route Component={EmailVerificationSuccessView} path={emailVerificationSuccessPath} />
          <Route Component={RequestMagicLinkView} path={requestMagicLinkPath} />
          <Route Component={VerifyMagicLinkView} path={verifyMagicLinkPath} />
          <Route Component={VerifyMagicLinkView} path={authMagicLinkPath} />
          <Route path="*" element={<RedirectToLogin />} />
        </SentryRoutes>
      </Suspense>
    </AuthLayout>
  );
};

AuthRouter.displayName = 'AuthRouter';
export default AuthRouter;
